import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { AddRounded, StairsRounded } from '@mui/icons-material';
import {
  EditRounded,
  DeleteOutlineRounded,
  SearchRounded,
  VisibilityRounded,
} from '@mui/icons-material';
import { collection, query, orderBy, onSnapshot, type DocumentData } from 'firebase/firestore';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { IconButton, InputAdornment, Tooltip } from '@mui/material';

import { CreateProgramDialog } from './components/CreateProgramDialog';
// EditProgramDialog replaced by ProgramDetailPage (/academic/programs/:id)
import { DeleteProgramDialog } from './components/DeleteProgramDialog';
import { DepartmentEditDialog, type DepartmentRecord } from './components/DepartmentEditDialog';
import { academicApi } from '../../lib/api/academic.api';
import { useConfirm } from '../../components/ConfirmProvider';

import { OpenTermDialog } from './components/OpenTermDialog';
import { EditTermDialog } from './components/EditTermDialog';
import { DeleteTermDialog } from './components/DeleteTermDialog';

import { CreateCourseDialog } from './components/CreateCourseDialog';
import { EditCourseDialog } from './components/EditCourseDialog';
import { DeleteCourseDialog } from './components/DeleteCourseDialog';
import { errorText } from '../../lib/error-text';
import { formatDate } from '../../lib/i18n-format';
import { useCanManageAcademics } from '../../hooks/use-can-manage-academics';
import { structureRowActions } from './academic-structure.actions';

type Tab = 'departments' | 'programs' | 'terms' | 'courses';

export function AcademicPage() {
  const puedeGestionar = useCanManageAcademics();
  // Lo que cada fila ofrece según el cargo (academic-structure.actions).
  const acts = {
    departments: structureRowActions({ canManage: puedeGestionar, kind: 'departments' }),
    programs: structureRowActions({ canManage: puedeGestionar, kind: 'programs' }),
    terms: structureRowActions({ canManage: puedeGestionar, kind: 'terms' }),
    courses: structureRowActions({ canManage: puedeGestionar, kind: 'courses' }),
  };
  const navigate = useNavigate();
  const { t } = useTranslation('sis');
  const confirm = useConfirm();
  const { currentTenantId } = useTenantStore();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => {
    const q = searchParams.get('tab');
    return q === 'programs' || q === 'courses' || q === 'terms' ? q : 'departments';
  });
  const [departments, setDepartments] = useState<DocumentData[]>([]);
  const [programs, setPrograms] = useState<DocumentData[]>([]);
  const [terms, setTerms] = useState<DocumentData[]>([]);
  const [courses, setCourses] = useState<DocumentData[]>([]);
  const [loading, setLoading] = useState(false);

  // Department dialogs
  const [deptDialogOpen, setDeptDialogOpen] = useState(false);
  const [editingDept, setEditingDept] = useState<DepartmentRecord | null>(null);

  // Modals state (Create)
  const [openProgram, setOpenProgram] = useState(false);
  const [openTerm, setOpenTerm] = useState(false);
  const [openCourse, setOpenCourse] = useState(false);

  // Modals state (Delete only — Edit navigates to /academic/programs/:id)
  const [deleteProgramParam, setDeleteProgramParam] = useState<DocumentData | null>(null);

  const [editTermParam, setEditTermParam] = useState<DocumentData | null>(null);
  const [deleteTermParam, setDeleteTermParam] = useState<DocumentData | null>(null);

  const [editCourseParam, setEditCourseParam] = useState<DocumentData | null>(null);
  const [deleteCourseParam, setDeleteCourseParam] = useState<DocumentData | null>(null);

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    if (!currentTenantId) return;
    setLoading(true);

    const loadState = { departments: false, programs: false, terms: false, courses: false };
    const checkLoading = () => {
      if (loadState.departments && loadState.programs && loadState.terms && loadState.courses) {
        setLoading(false);
      }
    };

    const unsub = [
      onSnapshot(
        query(collection(db, `tenants/${currentTenantId}/departments`), orderBy('name')),
        (s) => {
          setDepartments(s.docs.map((d) => ({ id: d.id, ...d.data() })));
          loadState.departments = true;
          checkLoading();
        },
        (err) => {
          console.error('[AcademicPage] Departments error:', err);
          loadState.departments = true;
          checkLoading();
        },
      ),
      onSnapshot(
        query(collection(db, `tenants/${currentTenantId}/programs`), orderBy('name')),
        (s) => {
          setPrograms(s.docs.map((d) => ({ id: d.id, ...d.data() })));
          loadState.programs = true;
          checkLoading();
        },
        (err) => {
          console.error('[AcademicPage] Programs error:', err);
          loadState.programs = true;
          checkLoading();
        },
      ),
      onSnapshot(
        query(collection(db, `tenants/${currentTenantId}/terms`), orderBy('startDate', 'desc')),
        (s) => {
          setTerms(s.docs.map((d) => ({ id: d.id, ...d.data() })));
          loadState.terms = true;
          checkLoading();
        },
        (err) => {
          console.error('[AcademicPage] Terms error:', err);
          loadState.terms = true;
          checkLoading();
        },
      ),
      onSnapshot(
        query(collection(db, `tenants/${currentTenantId}/courses`), orderBy('code')),
        (s) => {
          setCourses(s.docs.map((d) => ({ id: d.id, ...d.data() })));
          loadState.courses = true;
          checkLoading();
        },
        (err) => {
          console.error('[AcademicPage] Courses error:', err);
          loadState.courses = true;
          checkLoading();
        },
      ),
    ];
    return () => unsub.forEach((u) => u());
  }, [currentTenantId]);

  // Parent-lookup maps for showing parent identity columns in child tables.
  const departmentsById = useMemo(() => {
    const m = new Map<string, { name: string; code: string; primaryColor?: string }>();
    for (const d of departments) {
      m.set(d.id, {
        name: (d.name as string) ?? d.id,
        code: (d.code as string) ?? '',
        ...(d.primaryColor ? { primaryColor: d.primaryColor as string } : {}),
      });
    }
    return m;
  }, [departments]);

  const programsById = useMemo(() => {
    const m = new Map<string, { name: string; code: string; primaryColor?: string }>();
    for (const p of programs) {
      m.set(p.id, {
        name: (p.name as string) ?? p.id,
        code: (p.code as string) ?? '',
        ...(p.primaryColor ? { primaryColor: p.primaryColor as string } : {}),
      });
    }
    return m;
  }, [programs]);

  const filteredDepartments = useMemo(
    () =>
      departments.filter(
        (d) =>
          (showInactive || d.status !== 'inactive') &&
          (d.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            d.code?.toLowerCase().includes(searchQuery.toLowerCase())),
      ),
    [departments, searchQuery, showInactive],
  );

  const filteredPrograms = useMemo(
    () =>
      programs.filter(
        (p) =>
          (showInactive || p.status !== 'inactive') &&
          (p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.code?.toLowerCase().includes(searchQuery.toLowerCase())),
      ),
    [programs, searchQuery, showInactive],
  );

  const filteredTerms = useMemo(
    () => terms.filter((t) => t.name?.toLowerCase().includes(searchQuery.toLowerCase())),
    [terms, searchQuery],
  );

  const filteredCourses = useMemo(
    () =>
      courses.filter(
        (c) =>
          (showInactive || c.status !== 'inactive') &&
          (c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.code?.toLowerCase().includes(searchQuery.toLowerCase())),
      ),
    [courses, searchQuery, showInactive],
  );

  const counts = {
    departments: filteredDepartments.length,
    programs: filteredPrograms.length,
    terms: filteredTerms.length,
    courses: filteredCourses.length,
  };

  const handleCreateClick = () => {
    if (tab === 'departments') {
      setEditingDept(null);
      setDeptDialogOpen(true);
    } else if (tab === 'programs') setOpenProgram(true);
    else if (tab === 'terms') setOpenTerm(true);
    else if (tab === 'courses') setOpenCourse(true);
  };

  const handleDeleteDepartment = async (deptId: string) => {
    if (!currentTenantId) return;
    if (!(await confirm({ message: t('academic.deleteDeptConfirm'), confirmColor: 'error' })))
      return;
    try {
      await academicApi.deleteDepartment({ tenantId: currentTenantId, departmentId: deptId });
    } catch (e) {
      alert(errorText(e, t('academic.deleteError')));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            {t('academic.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('academic.subtitle')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {tab === 'programs' && (
            <Button
              variant="outlined"
              startIcon={<StairsRounded />}
              onClick={() => navigate('/academic/programs/ladder')}
            >
              {t('academic.ladderButton')}
            </Button>
          )}
          {/* CREAR ES TRABAJO DE REGISTRO. El docente llega a esta pantalla para
              CONSULTAR el catálogo; el servidor rechaza la escritura y un botón
              que falla se lee como un error del sistema. */}
          {puedeGestionar && (
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={handleCreateClick}
              sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
            >
              {tab === 'departments'
                ? t('academic.departments.new')
                : tab === 'programs'
                  ? t('academic.create.program')
                  : tab === 'terms'
                    ? t('academic.create.term')
                    : t('academic.create.course')}
            </Button>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v: Tab) => setTab(v)}
          sx={{
            flexGrow: 1,
            '& .MuiTab-root': { color: 'text.secondary', textTransform: 'none', fontWeight: 600 },
            '& .Mui-selected': { color: 'primary.main', fontWeight: 700 },
            '& .MuiTabs-indicator': { bgcolor: 'primary.main' },
          }}
        >
          {/* Reordered to match the academic hierarchy: Dept → Program → Course.
              Períodos (term/calendar) is orthogonal — placed last. */}
          <Tab
            label={`${t('academic.tabs.departments')} (${counts.departments})`}
            value="departments"
          />
          <Tab label={`${t('academic.tabs.programs')} (${counts.programs})`} value="programs" />
          <Tab label={`${t('academic.tabs.courses')} (${counts.courses})`} value="courses" />
          <Tab label={`${t('academic.tabs.terms')} (${counts.terms})`} value="terms" />
        </Tabs>

        <Stack direction="row" spacing={2} alignItems="center">
          {(tab === 'programs' || tab === 'courses') && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  {t('academic.showInactive')}
                </Typography>
              }
              sx={{ m: 0 }}
            />
          )}
          <TextField
            size="small"
            placeholder={t('academic.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: 250, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Stack>

      {loading ? (
        <Stack alignItems="center" py={8}>
          <CircularProgress />
        </Stack>
      ) : (
        <>
          {tab === 'departments' && (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 2, boxShadow: 'none' }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ '& th': { color: 'text.secondary', fontWeight: 600 } }}>
                    <TableCell>{t('academic.departments.columns.code')}</TableCell>
                    <TableCell>{t('academic.departments.columns.name')}</TableCell>
                    <TableCell>{t('academic.departments.columns.purpose')}</TableCell>
                    <TableCell>{t('academic.departments.columns.oads')}</TableCell>
                    <TableCell>{t('academic.departments.columns.status')}</TableCell>
                    {acts.departments.length > 0 && (
                      <TableCell align="right">
                        {t('academic.departments.columns.actions')}
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDepartments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: 'text.disabled', py: 6 }}>
                        {t('academic.departments.empty')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredDepartments.map((d) => (
                      <TableRow
                        key={d.id as string}
                        hover
                        sx={{ '& td': { color: 'text.primary' } }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor:
                                  (d.primaryColor as string | undefined) ?? 'action.disabled',
                                border: '1px solid',
                                borderColor: 'divider',
                              }}
                            />
                            <Typography fontFamily="monospace" color="primary.main">
                              {d.code as string}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{d.name as string}</TableCell>
                        <TableCell sx={{ maxWidth: 320, color: 'text.secondary' }}>
                          <Typography variant="body2" noWrap>
                            {(d.purpose as string | undefined) ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {Array.isArray(d.learningObjectives)
                            ? (d.learningObjectives as []).length
                            : 0}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={(d.status as string | undefined) ?? 'active'}
                            size="small"
                            color={d.status === 'active' || !d.status ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        {acts.departments.length > 0 && (
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              {acts.departments.includes('edit') && (
                                <Tooltip title={t('academic.tooltips.edit')}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const rec: DepartmentRecord = {
                                        id: d.id as string,
                                        name: d.name as string,
                                        code: d.code as string,
                                        status:
                                          (d.status as 'active' | 'inactive' | undefined) ??
                                          'active',
                                        ...(d.purpose ? { purpose: d.purpose as string } : {}),
                                        ...(d.learningObjectives
                                          ? {
                                              learningObjectives:
                                                d.learningObjectives as DepartmentRecord['learningObjectives'],
                                            }
                                          : {}),
                                        ...(d.primaryColor
                                          ? { primaryColor: d.primaryColor as string }
                                          : {}),
                                      };
                                      setEditingDept(rec);
                                      setDeptDialogOpen(true);
                                    }}
                                  >
                                    <EditRounded fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {acts.departments.includes('delete') && (
                                <Tooltip title={t('academic.tooltips.delete')}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteDepartment(d.id as string)}
                                  >
                                    <DeleteOutlineRounded fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 'programs' && (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 2, boxShadow: 'none' }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ '& th': { color: 'text.secondary', fontWeight: 600 } }}>
                    <TableCell>{t('academic.programs.columns.code')}</TableCell>
                    <TableCell>{t('academic.programs.columns.name')}</TableCell>
                    <TableCell>{t('academic.programs.columns.department')}</TableCell>
                    <TableCell>{t('academic.programs.columns.duration')}</TableCell>
                    <TableCell>{t('academic.programs.columns.status')}</TableCell>
                    {acts.programs.length > 0 && (
                      <TableCell align="right">{t('academic.programs.columns.actions')}</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPrograms.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: 'text.disabled', py: 6 }}>
                        {t('academic.programs.empty')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPrograms.map((p) => (
                      <TableRow
                        key={p.id as string}
                        hover
                        sx={{ '& td': { color: 'text.primary' } }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor:
                                  (p.primaryColor as string | undefined) ?? 'action.disabled',
                                border: '1px solid',
                                borderColor: 'divider',
                                flexShrink: 0,
                              }}
                            />
                            <Typography fontFamily="monospace" color="primary.main">
                              {p.code as string}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{p.name as string}</TableCell>
                        <TableCell>
                          {(() => {
                            const dept = p.departmentId
                              ? departmentsById.get(p.departmentId as string)
                              : undefined;
                            return dept ? (
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: dept.primaryColor ?? 'action.disabled',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                />
                                <Typography variant="body2">
                                  {dept.code ? `${dept.code} — ${dept.name}` : dept.name}
                                </Typography>
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                —
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell>{p.durationTerms as number}</TableCell>
                        <TableCell>
                          <Chip
                            label={p.status as string}
                            size="small"
                            color={p.status === 'active' ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        {acts.programs.length > 0 && (
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              {acts.programs.includes('edit') && (
                                <Tooltip title={t('academic.tooltips.edit')}>
                                  <IconButton
                                    size="small"
                                    onClick={() => navigate(`/academic/programs/${p.id as string}`)}
                                  >
                                    <EditRounded fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {acts.programs.includes('view') && (
                                <Tooltip title={t('academic.tooltips.view')}>
                                  <IconButton
                                    size="small"
                                    onClick={() => navigate(`/academic/programs/${p.id as string}`)}
                                  >
                                    <VisibilityRounded fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {acts.programs.includes('delete') && (
                                <Tooltip title={t('academic.tooltips.delete')}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setDeleteProgramParam(p)}
                                  >
                                    <DeleteOutlineRounded fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 'terms' && (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 2, boxShadow: 'none' }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ '& th': { color: 'text.secondary', fontWeight: 600 } }}>
                    <TableCell>{t('academic.terms.columns.name')}</TableCell>
                    <TableCell>{t('academic.terms.columns.start')}</TableCell>
                    <TableCell>{t('academic.terms.columns.end')}</TableCell>
                    <TableCell>{t('academic.terms.columns.status')}</TableCell>
                    {acts.terms.length > 0 && (
                      <TableCell align="right">{t('academic.terms.columns.actions')}</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTerms.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ color: 'text.disabled', py: 6 }}>
                        {t('academic.terms.empty')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTerms.map((tItem) => (
                      <TableRow
                        key={tItem.id as string}
                        hover
                        sx={{ '& td': { color: 'text.primary' } }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>{tItem.name as string}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {formatDate(tItem.startDate)}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {formatDate(tItem.endDate)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={tItem.status as string}
                            size="small"
                            color={
                              tItem.status === 'open'
                                ? 'success'
                                : tItem.status === 'closed'
                                  ? 'error'
                                  : 'default'
                            }
                            variant="outlined"
                          />
                        </TableCell>
                        {acts.terms.length > 0 && (
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              {acts.terms.includes('edit') && (
                                <Tooltip title={t('academic.tooltips.edit')}>
                                  <IconButton size="small" onClick={() => setEditTermParam(tItem)}>
                                    <EditRounded fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {acts.terms.includes('delete') && (
                                <Tooltip title={t('academic.tooltips.delete')}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setDeleteTermParam(tItem)}
                                  >
                                    <DeleteOutlineRounded fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 'courses' && (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 2, boxShadow: 'none' }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ '& th': { color: 'text.secondary', fontWeight: 600 } }}>
                    <TableCell>{t('academic.courses.columns.code')}</TableCell>
                    <TableCell>{t('academic.courses.columns.name')}</TableCell>
                    <TableCell>{t('academic.courses.columns.program')}</TableCell>
                    <TableCell>{t('academic.courses.columns.credits')}</TableCell>
                    <TableCell>{t('academic.courses.columns.status')}</TableCell>
                    {acts.courses.length > 0 && (
                      <TableCell align="right">{t('academic.courses.columns.actions')}</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCourses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: 'text.disabled', py: 6 }}>
                        {t('academic.courses.empty')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCourses.map((c) => (
                      <TableRow
                        key={c.id as string}
                        hover
                        sx={{ '& td': { color: 'text.primary' } }}
                      >
                        <TableCell>
                          <Typography fontFamily="monospace" color="primary.main">
                            {c.code as string}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{c.name as string}</TableCell>
                        <TableCell>
                          {(() => {
                            const prog = c.programId
                              ? programsById.get(c.programId as string)
                              : undefined;
                            return prog ? (
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: prog.primaryColor ?? 'action.disabled',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                />
                                <Typography variant="body2">
                                  {prog.code ? `${prog.code} — ${prog.name}` : prog.name}
                                </Typography>
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                —
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell>{c.credits as number}</TableCell>
                        <TableCell>
                          <Chip
                            label={(c.status as string) || 'active'}
                            size="small"
                            color={c.status === 'active' || !c.status ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        {acts.courses.length > 0 && (
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              {acts.courses.includes('edit') && (
                                <Tooltip title={t('academic.tooltips.edit')}>
                                  <IconButton
                                    size="small"
                                    onClick={() => navigate(`/academic/courses/${c.id}`)}
                                  >
                                    <EditRounded fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {acts.courses.includes('view') && (
                                <Tooltip title={t('academic.tooltips.view')}>
                                  <IconButton
                                    size="small"
                                    onClick={() => navigate(`/academic/courses/${c.id}`)}
                                  >
                                    <VisibilityRounded fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {acts.courses.includes('delete') && (
                                <Tooltip title={t('academic.tooltips.delete')}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setDeleteCourseParam(c)}
                                  >
                                    <DeleteOutlineRounded fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <CreateProgramDialog
        open={openProgram}
        onClose={() => setOpenProgram(false)}
        departments={departments
          .filter((d) => d.status !== 'inactive')
          .map((d) => ({ id: d.id as string, name: d.name as string, code: d.code as string }))}
      />

      <DepartmentEditDialog
        open={deptDialogOpen}
        onClose={() => {
          setDeptDialogOpen(false);
          setEditingDept(null);
        }}
        department={editingDept}
      />
      {/* EditProgramDialog removed — navigation to /academic/programs/:id */}
      <DeleteProgramDialog
        open={Boolean(deleteProgramParam)}
        onClose={() => setDeleteProgramParam(null)}
        program={deleteProgramParam}
      />

      <OpenTermDialog open={openTerm} onClose={() => setOpenTerm(false)} />
      <EditTermDialog
        open={Boolean(editTermParam)}
        onClose={() => setEditTermParam(null)}
        term={editTermParam}
      />
      <DeleteTermDialog
        open={Boolean(deleteTermParam)}
        onClose={() => setDeleteTermParam(null)}
        term={deleteTermParam}
      />

      <CreateCourseDialog open={openCourse} onClose={() => setOpenCourse(false)} />
      <EditCourseDialog
        open={Boolean(editCourseParam)}
        onClose={() => setEditCourseParam(null)}
        course={editCourseParam}
      />
      <DeleteCourseDialog
        open={Boolean(deleteCourseParam)}
        onClose={() => setDeleteCourseParam(null)}
        course={deleteCourseParam}
      />
    </Box>
  );
}
